import { useRef, useState, useEffect } from 'react';
import * as React from 'react';
import { VariableSizeList as List } from 'react-window';
import styled from 'styled-components';
import type { DisplayLayoutModeType } from '@sdk/schedule/types/DisplayLayoutModeType';
import type { LaborUISchemaType } from '@sdk/labor/types';
import type { SortMethodType } from '@sdk/schedule/types/SortMethodType';

import useWindowResize from '@web/common/hooks/useWindowResize';

import type { RowType } from '../types/RowType';
import type {
  OpenEventCallback,
  AddEventType,
  IsShiftLineVisibleType,
  ShouldShowPlusType,
  IsSpecialDayType,
  OptionsFilterType,
  ToggleHeaderTabsType,
  SetScheduleScrollOffsetType,
} from '../types';
import type { ToggleEventType, IsEventSelectedType } from '../hooks/useEventSelection';

import WindowedRowRenderer from './rows/WindowedRowRenderer';
import useScheduleScroll from './hooks/useScheduleScroll';

const Container = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

export type WindowRendererProps = {
  rows: Array<RowType>;
  currentPeriodDays: Array<string>;
  openEvent: OpenEventCallback;
  addUser: () => void;
  shiftAppearanceMode: DisplayLayoutModeType;
  addEvent: AddEventType;
  toggleEvent: ToggleEventType;
  hasSelectedEvents: boolean;
  isEventSelected: IsEventSelectedType;
  isShiftLineVisible: IsShiftLineVisibleType;
  groupMethod: SortMethodType;
  shouldShowPlus: ShouldShowPlusType;
  laborUISchema: LaborUISchemaType;
  isBusinessClosed: IsSpecialDayType;
  isHolidayPay: IsSpecialDayType;
  optionsFilter: OptionsFilterType;
  toggleHeaderTabs: ToggleHeaderTabsType;
  headerStatsHidden: boolean;
  headerTabsHidden: boolean;
  sidebarHidden: boolean;
  headerFiltersHidden: boolean;
  setScheduleScrollOffset: SetScheduleScrollOffsetType;
};
const WindowingRenderer: React.FC<React.PropsWithChildren<WindowRendererProps>> = (props) => {
  const {
    rows,
    hasSelectedEvents,
    groupMethod,
    toggleHeaderTabs,
    headerStatsHidden,
    headerTabsHidden,
    sidebarHidden,
    setScheduleScrollOffset,
    headerFiltersHidden,
    optionsFilter,
  } = props;
  const containerRef = useRef(null);
  const listRef = useRef(null);
  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  const itemCount = rows.length;
  const { onScroll } = useScheduleScroll({ toggleHeaderTabs });

  const measureDimensionsRef = useRef(() => {
    setDimensions({
      height: containerRef.current?.clientHeight || 0,
      width: containerRef.current?.clientWidth || 0,
    });
  }).current;

  useWindowResize(measureDimensionsRef);

  useEffect(() => {
    measureDimensionsRef();
  }, [containerRef, measureDimensionsRef]);

  useEffect(() => {
    const listElem = listRef.current._outerRef;
    const scrollOffset = listElem.offsetWidth - listElem.clientWidth;
    setScheduleScrollOffset(scrollOffset);
  });

  useEffect(() => {
    const timeoutId = setTimeout(measureDimensionsRef, 400);
    return (): void => clearTimeout(timeoutId);
  }, [
    headerStatsHidden,
    headerTabsHidden,
    sidebarHidden,
    headerFiltersHidden,
    measureDimensionsRef,
  ]);

  useEffect(() => {
    listRef.current.resetAfterIndex(0);
  }, [itemCount, groupMethod, optionsFilter]);

  const getRowSize = (index: number): number => {
    return rows[index].rowData.size;
  };

  const getItemKey = (index: number, data: WindowRendererProps): string => {
    return data.rows[index].id + data.rows[index].rowData.size;
  };

  return (
    <Container ref={containerRef}>
      <List
        ref={listRef}
        rows={rows}
        hasSelectedEvents={hasSelectedEvents}
        itemData={props}
        height={dimensions.height}
        itemCount={itemCount}
        itemSize={getRowSize}
        itemKey={getItemKey}
        width={dimensions.width}
        onScroll={onScroll}>
        {WindowedRowRenderer}
      </List>
    </Container>
  );
};

export default WindowingRenderer;
